import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { useReveal } from "@/hooks/useReveal";
import { submitMacdConnectLead, createMacdConnectPix } from "@/lib/api";
import {
  PERFIL_OPTIONS,
  TAMANHO_OPTIONS,
  TEMPO_OPTIONS,
  UFS,
} from "@/data/formOptions";

// Retratos: Gilson permanece na mesma URL institucional; Luciano é um asset
// local recortado da fotografia oficial fornecida.
const GILSON_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_oasys-academy/artifacts/ey99nph9_Sem%20t%C3%ADtulo%20-%2005%20de%20julho%20de%202026%20%C3%A0s%2016.27.14-5%20%285%29.png";
const LUCIANO_PORTRAIT = "/luciano.jpg";

const EDITION_SOURCE = "macd-connect-presencial-17ago2026";

const PILARES = [
  { letter: "M", title: "Marketing", description: "Presença, posicionamento e valor percebido." },
  { letter: "A", title: "Autoconhecimento", description: "Clareza, postura e consciência profissional." },
  { letter: "C", title: "Ciência do Cabelo", description: "Técnica com fundamento e domínio real." },
  { letter: "D", title: "Desenvolvimento Cognitivo-Comportamental", description: "Como decidir, aprender e sustentar o próximo passo." },
];

const AGENDA = [
  {
    time: "10h — 13h",
    who: "Gilson Ribeiro",
    title: "Gestão & Futuro",
    subtitle: "Antes de planejar o amanhã, é preciso compreender o que está acontecendo hoje.",
    tone: "primary",
  },
  { time: "13h — 14h", who: "Intervalo para almoço", title: null, tone: "muted" },
  {
    time: "14h — 17h30",
    who: "Luciano Marques",
    title: "Por Que Você Desiste?",
    subtitle: "A Ciência Mental por trás do Sucesso Orgânico — e como parar de se sabotar.",
    tone: "primary",
  },
  {
    time: "17h30 — 18h30",
    who: "Entrega de diplomas",
    title: null,
    subtitle: "Confraternização de encerramento.",
    tone: "closing",
  },
];

const FAQ = [
  {
    q: "Onde acontece o encontro?",
    a: "Em São Paulo. O endereço exato é enviado por WhatsApp a partir da confirmação da inscrição.",
  },
  {
    q: "O evento é gravado?",
    a: "É uma experiência presencial. Não há transmissão nem gravação disponibilizada depois.",
  },
  {
    q: "Como é feita a inscrição?",
    a: "Você preenche o formulário abaixo. Nossa equipe entra em contato pelo WhatsApp para confirmar a vaga e enviar os detalhes.",
  },
  {
    q: "Posso levar minha equipe?",
    a: "Sim. Cada participante precisa de uma vaga individual. Nos avise pelo WhatsApp para reservar em conjunto.",
  },
  {
    q: "Vai ter certificado?",
    a: "Sim. Certificado institucional Oasys Academy — MACD Connect Presencial, 31/08/2026.",
  },
];

export default function MacdConnect() {
  useReveal();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white antialiased selection:bg-[#f4c542]/40">
      <Navbar />
      <Hero />
      <MercadoMudou />
      <OQueE />
      <DoisOlhares />
      <BlocoGilson />
      <BlocoLuciano />
      <Programacao />
      <MetodologiaMACD />
      <ParaQuemE />
      <SalonUP />
      <Investimento />
      <FaqSection />
      <Fechamento />
      <Footer />
    </div>
  );
}

// ============================================================
// HERO
// ============================================================
function Hero() {
  return (
    <section
      data-testid="mc-hero"
      className="relative overflow-hidden bg-[#0a0a0a] pt-32 pb-24 md:pt-40 md:pb-32"
    >
      {/* subtle golden radial */}
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.12] blur-3xl"
        style={{ background: "radial-gradient(circle, #f4c542 0%, transparent 70%)" }}
      />
      <div className="relative max-w-[1240px] mx-auto px-6 md:px-10">
        <div className="reveal flex items-center gap-4 mb-10">
          <span className="inline-block w-10 h-[3px] bg-[#f4c542]" />
          <span className="eyebrow text-white/60">
            Oasys Academy · Edição Presencial
          </span>
        </div>

        <h1
          data-testid="mc-hero-title"
          className="reveal font-display leading-[0.94] tracking-[-0.02em] text-white text-[3rem] sm:text-[3.75rem] md:text-[5.5rem] lg:text-[6.5rem]"
        >
          MACD Connect<br />
          <em className="italic text-[#f4c542]">Presencial.</em>
        </h1>

        <div className="reveal mt-14 flex flex-col gap-2 max-w-2xl">
          <span
            data-testid="mc-hero-date"
            className="font-display text-2xl md:text-3xl text-white tracking-tight"
          >
            31 <em className="italic">de agosto de 2026</em>
          </span>
          <span data-testid="mc-hero-time" className="eyebrow text-[#f4c542]">
            Das 10h às 18h30
          </span>
          <span data-testid="mc-hero-city" className="eyebrow text-white/70">
            São Paulo · Presencial
          </span>
        </div>

        <div className="reveal mt-12 flex flex-wrap items-baseline gap-x-10 gap-y-4">
          <div>
            <div className="eyebrow text-white/45">Vagas</div>
            <div
              data-testid="mc-hero-vagas"
              className="font-display text-3xl md:text-4xl tracking-tight text-white"
            >
              40 <em className="italic text-white/60 text-2xl">limitadas</em>
            </div>
          </div>
          <div>
            <div className="eyebrow text-white/45">Ingresso</div>
            <div
              data-testid="mc-hero-preco"
              className="font-display text-3xl md:text-4xl tracking-tight text-[#f4c542]"
            >
              R$ 97
            </div>
          </div>
        </div>

        <p className="reveal mt-14 max-w-xl text-lg md:text-xl leading-relaxed text-white/75">
          Um dia inteiro dedicado a{" "}
          <em className="italic text-white">transformar intenção em direção</em>.
          Negócio e comportamento no mesmo palco.
        </p>

        <div className="reveal mt-10 flex flex-wrap items-center gap-4">
          <a
            href="#inscricao"
            data-testid="mc-hero-cta"
            className="btn-primary"
          >
            Garantir minha vaga
            <span aria-hidden>→</span>
          </a>
          <a
            href="#programacao"
            data-testid="mc-hero-cta-secondary"
            className="text-sm uppercase tracking-[0.14em] text-white/70 hover:text-white border-b border-white/30 hover:border-white pb-1"
          >
            Ver a programação
          </a>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// O MERCADO MUDOU
// ============================================================
function MercadoMudou() {
  return (
    <section
      data-testid="mc-mercado"
      className="py-24 md:py-32 bg-[#f4f2ec] text-[#0a0a0a]"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 reveal">
          <span className="eyebrow text-[#8a8a8a]">01 · O mercado mudou</span>
        </div>
        <div className="lg:col-span-8 reveal space-y-8">
          <h2 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
            A técnica continua importante.{" "}
            <em className="italic">Mas ela não decide mais sozinha quem cresce.</em>
          </h2>
          <div className="space-y-6 text-lg leading-[1.75] text-[#1f1f1f] max-w-2xl">
            <p>
              Hoje o profissional precisa compreender dados, tempo, imagem,
              comportamento, cliente, valor, negócio e futuro — não como
              conteúdos separados, mas como camadas de uma mesma decisão.
            </p>
            <p>
              Quem lê o próprio mercado com atenção percebe: não é falta de
              esforço. É falta de leitura. Falta de direção.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// O QUE É
// ============================================================
function OQueE() {
  return (
    <section
      data-testid="mc-oque-e"
      className="py-24 md:py-32 bg-[#0a0a0a] text-white"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 reveal">
          <span className="eyebrow text-white/45 mb-6 block">02 · O que é</span>
          <h2 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight text-white">
            Uma leitura completa da carreira em{" "}
            <em className="italic text-[#f4c542]">um único dia.</em>
          </h2>
        </div>
        <div className="lg:col-span-7 reveal space-y-8 text-lg leading-[1.8] text-white/80 max-w-2xl">
          <p>
            <span className="text-white font-medium">MACD Connect Presencial</span>{" "}
            é a evolução da experiência online da Academy — desta vez presencial,
            em São Paulo, em <em className="italic">31 de agosto de 2026</em>.
          </p>
          <p>
            Não é um evento de dois palestrantes. É uma experiência construída
            em dois movimentos: <em className="italic">negócio</em> e{" "}
            <em className="italic">comportamento</em>. Reunidos no mesmo dia
            porque um não funciona sem o outro.
          </p>
          <p className="pt-4 pl-6 md:pl-8 border-l border-[#f4c542]/60 font-display text-xl md:text-2xl leading-snug text-white">
            Planejar o futuro exige números, direção e estratégia.
            <br />
            <em className="italic text-[#f4c542]">
              Sustentar esse futuro exige comportamento.
            </em>
          </p>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// DOIS OLHARES — frase-ponte
// ============================================================
function DoisOlhares() {
  return (
    <section
      data-testid="mc-dois-olhares"
      className="py-20 md:py-28 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center reveal">
        <span className="eyebrow text-white/45 mb-8 block">03 · Dois olhares para o futuro</span>
        <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-white">
          Dois olhares.{" "}
          <em className="italic text-[#f4c542]">Um mesmo futuro.</em>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto text-lg text-white/70 leading-relaxed">
          <span className="text-white">Gilson Ribeiro</span> abre o dia trabalhando o
          negócio que precisa ser compreendido.{" "}
          <span className="text-white">Luciano Marques</span> conduz o segundo ato,
          sobre o comportamento necessário para sustentar essa construção.
        </p>
      </div>
    </section>
  );
}

// ============================================================
// GILSON RIBEIRO — Gestão & Futuro
// ============================================================
function BlocoGilson() {
  return (
    <section
      data-testid="mc-bloco-gilson"
      className="py-24 md:py-32 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 reveal">
          <div className="aspect-[4/5] overflow-hidden bg-[#141414]">
            <img
              src={GILSON_PORTRAIT}
              alt="Retrato de Gilson Ribeiro"
              className="w-full h-full object-cover object-center"
              data-testid="mc-gilson-portrait"
              loading="lazy"
            />
          </div>
        </div>
        <div className="lg:col-span-7 reveal">
          <span className="eyebrow text-[#f4c542]">10h — 13h · Primeiro ato</span>
          <div className="mt-3 text-sm uppercase tracking-[0.24em] text-white/50">
            Gilson Ribeiro · Fundador Oasys Academy
          </div>
          <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
            Gestão &<br />
            <em className="italic text-[#f4c542]">Futuro.</em>
          </h2>
          <p className="mt-6 font-display italic text-xl md:text-2xl text-white/85 leading-snug max-w-xl">
            Antes de planejar o amanhã, é preciso compreender o que está
            acontecendo hoje.
          </p>

          <div className="mt-10 space-y-6 text-[15px] md:text-base leading-[1.8] text-white/75 max-w-xl">
            <p>
              Um bloco construído em cima de perguntas que a maioria dos
              profissionais nunca fez com clareza.{" "}
              <em className="italic text-white/90">Quanto você quer ganhar?</em>{" "}
              Quanto precisa faturar para isso? Quanto custa o seu dia?
            </p>
            <p>
              Quanto vale uma hora do seu trabalho? Quantos horários você
              realmente possui? Quantos estão ocupados? Qual é o seu ticket
              médio? Quantas clientes retornam, quantas indicam,{" "}
              <em className="italic">quantas desapareceram</em>?
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 max-w-xl">
            {[
              "Dados", "Tempo", "Imagem", "Faturamento", "Custos",
              "Valor da hora", "Agenda", "Recorrência", "Clientes",
              "Processos", "Decisão", "Futuro",
            ].map((t) => (
              <span
                key={t}
                className="text-[11px] tracking-[0.18em] uppercase text-white/45"
              >
                · {t}
              </span>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 max-w-xl">
            <p className="font-display text-xl md:text-[1.6rem] leading-snug tracking-tight text-white">
              Sem diagnóstico, não existe planejamento.
              <br />
              <em className="italic text-[#f4c542]">Existe apenas expectativa.</em>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// LUCIANO MARQUES — Por Que Você Desiste?
// ============================================================
function BlocoLuciano() {
  return (
    <section
      data-testid="mc-bloco-luciano"
      className="py-24 md:py-32 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7 reveal order-2 lg:order-1">
          <span className="eyebrow text-[#f4c542]">14h — 17h30 · Segundo ato</span>
          <div className="mt-3 text-sm uppercase tracking-[0.24em] text-white/50">
            Luciano Marques · Convidado principal
          </div>
          <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
            Por que você<br />
            <em className="italic text-[#f4c542]">desiste?</em>
          </h2>
          <p className="mt-6 font-display italic text-xl md:text-2xl text-white/85 leading-snug max-w-xl">
            A Ciência Mental por trás do Sucesso Orgânico — e como parar de
            se sabotar.
          </p>

          <div className="mt-10 space-y-6 text-[15px] md:text-base leading-[1.8] text-white/75 max-w-xl">
            <p>
              O que separa o profissional comum do extraordinário raramente
              está na técnica. Está na forma como ele conduz{" "}
              <em className="italic">a própria mente</em> quando ninguém
              está vendo.
            </p>
            <p>
              Como nascem as crenças que sustentam ou paralisam uma carreira.
              O poder invisível do{" "}
              <em className="italic">looping da autocriação</em>. E o que
              acontece quando o racional e o emocional deixam de ser
              adversários.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 max-w-xl">
            {[
              "Comportamento", "Crenças", "Constância",
              "Autoconhecimento", "Performance",
              "Círculo de Excelência",
            ].map((t) => (
              <span
                key={t}
                className="text-[11px] tracking-[0.18em] uppercase text-white/45"
              >
                · {t}
              </span>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 max-w-xl">
            <p className="font-display text-xl md:text-[1.6rem] leading-snug tracking-tight text-white">
              Nenhuma decisão se sustenta{" "}
              <em className="italic text-[#f4c542]">
                sem comportamento para carregá-la.
              </em>
            </p>
          </div>
        </div>
        <div className="lg:col-span-5 reveal order-1 lg:order-2">
          <div className="aspect-[4/5] overflow-hidden bg-[#141414]">
            <img
              src={LUCIANO_PORTRAIT}
              alt="Retrato de Luciano Marques"
              className="w-full h-full object-cover object-center"
              data-testid="mc-luciano-portrait"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// PROGRAMAÇÃO
// ============================================================
function Programacao() {
  return (
    <section
      id="programacao"
      data-testid="mc-programacao"
      className="py-24 md:py-32 bg-[#f4f2ec] text-[#0a0a0a]"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10">
        <div className="reveal mb-14 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <span className="eyebrow text-[#8a8a8a]">04 · Programação</span>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              31 de agosto · <em className="italic">um dia inteiro.</em>
            </h2>
            <p className="mt-4 text-lg text-[#4a4a4a]">
              Evento das <span className="text-[#0a0a0a] font-medium">10h às 18h30</span>.
              São Paulo.
            </p>
          </div>
        </div>

        <div className="space-y-6 max-w-4xl mx-auto">
          {AGENDA.map((slot, i) => (
            <div
              key={i}
              data-testid={`mc-agenda-slot-${i}`}
              className={`reveal grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 ${
                slot.tone === "closing" ? "py-6" : "py-8"
              } border-t ${
                i === AGENDA.length - 1 ? "border-b" : ""
              } border-[#d4cfc0]`}
            >
              <div className="md:col-span-3">
                <div
                  className={`font-display tracking-tight ${
                    slot.tone === "muted"
                      ? "text-[#8a8a8a] italic text-2xl md:text-[1.7rem]"
                      : slot.tone === "closing"
                      ? "text-[#3a3a3a] text-xl md:text-[1.25rem]"
                      : "text-[#0a0a0a] text-2xl md:text-[1.7rem]"
                  }`}
                >
                  {slot.time}
                </div>
              </div>
              <div className="md:col-span-9">
                <div
                  className={`eyebrow ${
                    slot.tone === "closing" ? "text-[#8a8a8a]" : "text-[#8a8a8a]"
                  }`}
                >
                  {slot.who}
                </div>
                {slot.title && (
                  <div className="mt-2 font-display text-2xl md:text-3xl leading-tight tracking-tight text-[#0a0a0a]">
                    {slot.title}
                  </div>
                )}
                {slot.subtitle && (
                  <p
                    className={`mt-2 font-display italic leading-snug ${
                      slot.tone === "closing"
                        ? "text-[15px] md:text-base text-[#4a4a4a] max-w-lg"
                        : "text-lg md:text-xl text-[#2a2a2a] max-w-2xl"
                    }`}
                  >
                    {slot.subtitle}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// METODOLOGIA MACD
// ============================================================
function MetodologiaMACD() {
  return (
    <section
      data-testid="mc-metodologia"
      className="py-24 md:py-32 bg-[#0a0a0a] text-white"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10">
        <div className="reveal mb-14 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6">
            <span className="eyebrow text-white/45">05 · Metodologia MACD</span>
            <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
              Quatro pilares.{" "}
              <em className="italic text-[#f4c542]">Uma visão inteira.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 space-y-4 text-white/75 leading-[1.7] text-[15px] md:text-base">
            <p>
              Técnica, mercado, identidade e comportamento precisam conversar.
            </p>
            <p className="pt-4 mt-2 border-t border-white/15 font-display text-xl md:text-[1.4rem] leading-snug tracking-tight text-white">
              Nenhum deles decide sozinho{" "}
              <em className="italic text-[#f4c542]">quem cresce.</em>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {PILARES.map((p) => (
            <div
              key={p.letter}
              data-testid={`mc-pilar-${p.letter}`}
              className="bg-[#0a0a0a] p-8 md:p-10 reveal"
            >
              <div className="font-display text-6xl md:text-7xl text-[#f4c542] leading-none">
                {p.letter}
              </div>
              <div className="mt-6 text-[11px] tracking-[0.24em] uppercase text-white/55">
                {p.title}
              </div>
              <div className="mt-3 text-[15px] leading-[1.6] text-white/75 max-w-[220px]">
                {p.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// PARA QUEM É
// ============================================================
function ParaQuemE() {
  return (
    <section
      data-testid="mc-para-quem"
      className="py-24 md:py-32 bg-[#f4f2ec] text-[#0a0a0a]"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 reveal">
          <span className="eyebrow text-[#8a8a8a]">06 · Para quem é</span>
          <h2 className="mt-6 font-display text-3xl md:text-[3rem] leading-[1.02] tracking-tight">
            Para quem já entendeu que a técnica{" "}
            <em className="italic">não sustenta sozinha.</em>
          </h2>
        </div>
        <div className="lg:col-span-8 reveal grid sm:grid-cols-3 gap-6">
          {[
            {
              tag: "Profissional",
              body: "Que domina a técnica, entrega bem, mas depende demais das variações do mês, das indicações ou do algoritmo.",
            },
            {
              tag: "Dono / Gestor",
              body: "Que precisa ler o próprio salão com dados, ampliar o valor médio e construir previsibilidade.",
            },
            {
              tag: "Auxiliar",
              body: "Que quer entrar na carreira com base, método e postura — não com achismo.",
            },
          ].map((p) => (
            <div
              key={p.tag}
              className="bg-white border border-[#0a0a0a]/8 p-8 flex flex-col"
            >
              <div className="eyebrow text-[#8a8a8a]">{p.tag}</div>
              <div className="mt-4 text-[15px] leading-[1.65] text-[#2a2a2a]">
                {p.body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// SALONUP (bloco discreto)
// ============================================================
function SalonUP() {
  return (
    <section
      data-testid="mc-salonup"
      className="py-24 md:py-32 bg-[#0a0a0a] text-white border-t border-white/5"
    >
      <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center reveal">
        <span className="eyebrow text-[#f4c542]">07 · Depois do evento</span>
        <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
          Conhecimento precisa continuar{" "}
          <em className="italic text-[#f4c542]">depois do evento.</em>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto text-lg leading-relaxed text-white/75">
          A Academy ensina a pensar. Durante o dia, Gilson apresentará o{" "}
          <span className="text-white">SalonUP</span> como consequência
          prática dessa leitura — a ferramenta que transforma dados,
          decisão e processo em operação diária.
        </p>
        <p className="mt-6 max-w-xl mx-auto text-[15px] italic text-white/55">
          Não é o protagonista do dia. É a continuidade natural dele.
        </p>
      </div>
    </section>
  );
}

// ============================================================
// INVESTIMENTO + FORMULÁRIO
// ============================================================
function Investimento() {
  const navigate = useNavigate();
  const [values, setValues] = useState({
    nome: "",
    whatsapp: "",
    email: "",
    perfil: "",
    tamanho_salao: "",
    tempo_profissao: "",
    cidade: "",
    estado: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const setField = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((e2) => ({ ...e2, [k]: "" }));
  };
  const setPerfil = (p) => {
    setValues((v) => ({ ...v, perfil: p }));
    setErrors((e) => ({ ...e, perfil: "" }));
  };

  const validate = () => {
    const e = {};
    if (values.nome.trim().length < 2) e.nome = "Nome muito curto.";
    if (values.whatsapp.replace(/\D/g, "").length < 10)
      e.whatsapp = "Informe WhatsApp com DDD.";
    // E-mail: validação leve de UX (input type=email já cobre formato).
    const rawEmail = values.email.trim();
    if (!rawEmail) e.email = "E-mail obrigatório.";
    else if (!rawEmail.includes("@") || rawEmail.length < 5)
      e.email = "E-mail inválido.";
    if (!values.perfil) e.perfil = "Selecione um perfil.";
    if (!values.tamanho_salao) e.tamanho_salao = "Selecione.";
    if (!values.tempo_profissao) e.tempo_profissao = "Selecione.";
    if (values.cidade.trim().length < 2) e.cidade = "Cidade obrigatória.";
    if (!values.estado) e.estado = "Selecione UF.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Step 1 — persist the lead. Success here means "inscrição recebida".
      const lead = await submitMacdConnectLead({
        ...values,
        email: values.email.trim(),
        source: EDITION_SOURCE,
      });
      const firstName = values.nome.split(" ")[0];

      // If the returning lead is already paid, skip PIX and go straight
      // to the confirmation page.
      if (lead?.resume === "already_paid") {
        navigate("/macd-connect/confirmado", {
          state: { nome: firstName, lead_id: lead.id, paid: true },
        });
        return;
      }

      // Step 2 — try to create/resume the PIX. Failures here MUST NOT lose
      // the lead: the payment page handles the retry flow.
      let pix = null;
      let pixError = null;
      try {
        pix = await createMacdConnectPix({ lead_id: lead.id });
      } catch (err) {
        pixError = err?.detail || err?.message || "erro_desconhecido";
      }

      navigate("/macd-connect/pagamento", {
        state: {
          lead_id: lead.id,
          nome: firstName,
          pix,
          pixError,
        },
      });
    } catch (err) {
      setServerError(
        "Não foi possível registrar sua inscrição agora. Tente novamente em instantes.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inscricao"
      data-testid="mc-form-section"
      className="py-24 md:py-32 bg-[#f4f2ec] text-[#0a0a0a]"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 reveal">
          <span className="eyebrow text-[#8a8a8a]">08 · Investimento</span>
          <h2 className="mt-6 font-display text-4xl md:text-[3.25rem] leading-[1.02] tracking-tight">
            Uma vaga.{" "}
            <em className="italic">Um dia inteiro.</em>
          </h2>

          <div className="mt-10 flex flex-col gap-2">
            <span
              data-testid="mc-form-dates"
              className="font-display text-xl md:text-2xl text-[#0a0a0a] tracking-tight"
            >
              31 <em className="italic">de agosto de 2026</em>
            </span>
            <span data-testid="mc-form-time" className="eyebrow text-[#0a0a0a]">
              Das 10h às 18h30
            </span>
            <span className="eyebrow text-[#6b6b6b]">
              São Paulo · Presencial
            </span>
          </div>

          <div className="mt-10 pt-8 border-t border-[#0a0a0a]/15">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#8a8a8a]">
              Ingresso individual
            </div>
            <div className="mt-2 font-display text-5xl md:text-6xl tracking-tight text-[#0a0a0a]">
              R$ 97
            </div>
            <div className="mt-3 text-[15px] text-[#4a4a4a]">
              40 vagas · confirmação por WhatsApp após inscrição.
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 reveal">
          <form
            onSubmit={onSubmit}
            data-testid="mc-form"
            className="bg-white border border-[#0a0a0a]/8 p-8 md:p-10"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Nome completo" error={errors.nome} full>
                <input
                  type="text"
                  value={values.nome}
                  onChange={setField("nome")}
                  data-testid="mc-input-nome"
                  className="input-line"
                />
              </Field>
              <Field label="WhatsApp (com DDD)" error={errors.whatsapp} full>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={values.whatsapp}
                  onChange={setField("whatsapp")}
                  data-testid="mc-input-whatsapp"
                  placeholder="11 91234-5678"
                  className="input-line"
                />
              </Field>
              <Field label="E-mail" error={errors.email} full>
                <input
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={setField("email")}
                  data-testid="mc-input-email"
                  placeholder="voce@exemplo.com"
                  className="input-line"
                  required
                />
              </Field>

              <Field label="Você é" error={errors.perfil} full>
                <div className="flex flex-wrap gap-2 mt-2">
                  {PERFIL_OPTIONS.map((p) => (
                    <button
                      type="button"
                      key={p.value}
                      onClick={() => setPerfil(p.value)}
                      data-testid={`mc-perfil-${p.value}`}
                      className={`px-4 py-2 border text-sm transition ${
                        values.perfil === p.value
                          ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                          : "bg-transparent text-[#0a0a0a] border-[#0a0a0a]/25 hover:border-[#0a0a0a]"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Tamanho do salão / equipe" error={errors.tamanho_salao}>
                <select
                  value={values.tamanho_salao}
                  onChange={setField("tamanho_salao")}
                  data-testid="mc-select-tamanho"
                  className="input-line"
                >
                  <option value="">—</option>
                  {TAMANHO_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Tempo de profissão" error={errors.tempo_profissao}>
                <select
                  value={values.tempo_profissao}
                  onChange={setField("tempo_profissao")}
                  data-testid="mc-select-tempo"
                  className="input-line"
                >
                  <option value="">—</option>
                  {TEMPO_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Cidade" error={errors.cidade}>
                <input
                  type="text"
                  value={values.cidade}
                  onChange={setField("cidade")}
                  data-testid="mc-input-cidade"
                  className="input-line"
                />
              </Field>

              <Field label="UF" error={errors.estado}>
                <select
                  value={values.estado}
                  onChange={setField("estado")}
                  data-testid="mc-select-estado"
                  className="input-line"
                >
                  <option value="">—</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </Field>
            </div>

            {serverError && (
              <p
                data-testid="mc-form-server-error"
                className="mt-6 text-sm text-red-700"
              >
                {serverError}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="mc-form-submit"
              className="btn-primary mt-10"
            >
              {submitting ? "Enviando…" : "Garantir minha vaga"}
              {!submitting && <span aria-hidden>→</span>}
            </button>

            <p className="mt-6 text-xs text-[#6b6b6b] max-w-md leading-relaxed">
              Ao enviar, você autoriza o contato da Oasys Academy pelo
              WhatsApp para confirmação da vaga e envio dos detalhes do
              evento.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, error, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] tracking-[0.24em] uppercase text-[#8a8a8a] mb-2">
        {label}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-700">{error}</p>
      )}
    </div>
  );
}

// ============================================================
// FAQ
// ============================================================
function FaqSection() {
  const [open, setOpen] = useState(-1);
  return (
    <section
      data-testid="mc-faq"
      className="py-24 md:py-32 bg-[#0a0a0a] text-white"
    >
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <div className="reveal mb-14">
          <span className="eyebrow text-white/45">09 · FAQ</span>
          <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
            O que é bom{" "}
            <em className="italic text-[#f4c542]">saber antes.</em>
          </h2>
        </div>

        <div className="divide-y divide-white/10 border-t border-b border-white/10">
          {FAQ.map((it, i) => {
            const isOpen = open === i;
            return (
              <button
                key={i}
                type="button"
                data-testid={`mc-faq-item-${i}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full text-left py-6 md:py-7 flex gap-6 items-start hover:bg-white/[0.02] transition-colors"
              >
                <span className="font-display text-lg text-[#f4c542] w-8 shrink-0 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1">
                  <span className="block font-display text-lg md:text-xl text-white">
                    {it.q}
                  </span>
                  {isOpen && (
                    <span className="block mt-3 text-[15px] leading-relaxed text-white/70">
                      {it.a}
                    </span>
                  )}
                </span>
                <span
                  aria-hidden
                  className={`shrink-0 text-white/45 transition-transform ${
                    isOpen ? "rotate-45" : ""
                  }`}
                >
                  +
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FECHAMENTO
// ============================================================
function Fechamento() {
  return (
    <section
      data-testid="mc-fechamento"
      className="py-24 md:py-32 bg-[#0a0a0a] text-white border-t border-white/5"
    >
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center reveal">
        <span className="eyebrow text-white/45">10 · 31 de agosto</span>
        <h2 className="mt-6 font-display text-4xl md:text-[4rem] leading-[1.02] tracking-tight">
          Transformar intenção<br />
          <em className="italic text-[#f4c542]">em direção.</em>
        </h2>
        <div className="mt-10 flex flex-col gap-2 items-center">
          <span
            data-testid="mc-fechamento-dates"
            className="font-display text-2xl md:text-3xl text-white tracking-tight"
          >
            31 <em className="italic">de agosto de 2026</em>
          </span>
          <span data-testid="mc-fechamento-time" className="eyebrow text-[#f4c542]">
            Das 10h às 18h30
          </span>
          <span className="eyebrow text-white/60">
            São Paulo · Presencial · 40 vagas
          </span>
        </div>
        <a
          href="#inscricao"
          data-testid="mc-fechamento-cta"
          className="btn-primary mt-12 inline-flex"
        >
          Garantir minha vaga
          <span aria-hidden>→</span>
        </a>
      </div>
    </section>
  );
}
